import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Sidebar from '@/components/Sidebar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import IntegrationsTab from '@/components/settings/IntegrationsTab';
import { Badge } from "@/components/ui/badge";
import { useNavigate } from 'react-router-dom';
import { Lock, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getUserSubscription, isBasicPlan } from '@/services/subscriptionService';
import { useAuth } from '@/components/auth/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// Define types for the ATS data
interface ExternalATSIntegration {
  category: string;
  label: string;
  id: string;
  logo_short: string;
  logo_full: string;
}

interface ATSListResponse {
  success: boolean;
  data: ExternalATSIntegration[];
}

// Define types for the proxy component props
interface IntegrationsProxyProps {
  isBasicPlan: boolean;
  onUpgradeClick: () => void;
  atsList: ExternalATSIntegration[];
}

// Create a proxy component that wraps IntegrationsTab to intercept clicks
const IntegrationsProxy: React.FC<IntegrationsProxyProps> = ({ isBasicPlan, onUpgradeClick, atsList }) => {
  // Clone the component with a custom wrapper to intercept clicks
  useEffect(() => {
    const addPremiumOverlays = () => {
      // Only add overlays if user is on basic plan
      if (!isBasicPlan) return;
      
      // Find all ATS integration cards
      setTimeout(() => {
        const integrationCards = document.querySelectorAll('[data-testid="ats-integration-card"]');
        if (!integrationCards || integrationCards.length === 0) {
          // Try again if elements aren't loaded yet
          setTimeout(addPremiumOverlays, 100);
          return;
        }
        
        // Add premium overlay to each card
        integrationCards.forEach(card => {
          // Skip if already processed
          if (card.querySelector('.premium-overlay')) return;
          
          // Create overlay element
          const overlay = document.createElement('div');
          overlay.className = 'premium-overlay absolute inset-0 bg-black/5 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity';
          overlay.innerHTML = `
            <div class="bg-white/95 p-3 rounded-md shadow-md text-center">
              <span class="text-amber-600 font-semibold flex items-center justify-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-lock"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                Premium Feature
              </span>
            </div>
          `;
          
          // Replace original click handler
          card.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            onUpgradeClick();
            return false;
          }, true);
          
          // Add overlay to card
          card.appendChild(overlay);
          
          // Ensure card has relative positioning for absolute overlay
          (card as HTMLElement).style.position = 'relative';
        });
      }, 100);
    };
    
    // Run initially and also when DOM changes
    addPremiumOverlays();
    
    // Use MutationObserver to detect when new integration cards are added
    const observer = new MutationObserver(addPremiumOverlays);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => observer.disconnect();
  }, [isBasicPlan, onUpgradeClick]);
  
  // Memoize the rendering of IntegrationsTab to prevent unnecessary re-renders
  return useMemo(() => (
    <IntegrationsTab atsList={atsList} />
  ), [atsList]);
};

const Settings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isBasicPlanUser, setIsBasicPlanUser] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [atsList, setAtsList] = useState<ExternalATSIntegration[]>([]);
  const [dataFetched, setDataFetched] = useState(false); // Track if we've already fetched data
  const [activeTab, setActiveTab] = useState("integrations");

  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/auth/login', { replace: true });
    }
  }, [user, navigate]);

  // Fetch ATS list from JSON file only once
  useEffect(() => {
    const fetchATSList = async () => {
      // Check if we already have the list cached in localStorage
      const cached = localStorage.getItem('roundone_ats_list');

      // Even if we have a cache, we still try to fetch a fresh copy in the background,
      // but we show the cached version immediately so the UI never gets stuck.
      if (cached && !dataFetched) {
        try {
          const parsed: ATSListResponse = JSON.parse(cached);
          if (parsed.success && parsed.data) {
            setAtsList(parsed.data);
            setDataFetched(true);
          }
        } catch (_) {
          // Ignore JSON parse errors and fall back to network request
        }
      }

      const response = await fetch('/ats-logos/atslist.json', {
        // Force a network request (so we\'re not accidentally getting a 304 with an empty body)
        cache: 'no-cache',
      });

      // If we receive a 304 (Not Modified), keep using the cached version.
      if (response.status === 304) {
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch ATS list');
      }

      const data: ATSListResponse = await response.json();
      if (data.success && data.data) {
        localStorage.setItem('roundone_ats_list', JSON.stringify(data));
        setAtsList(data.data);
        setDataFetched(true); // Mark that we've fetched the data
      }
    };

    fetchATSList();
  }, [dataFetched, atsList.length]);

  useEffect(() => {
    const fetchUserSubscription = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const subscription = await getUserSubscription(user.id);
        setIsBasicPlanUser(isBasicPlan(subscription));
      } catch (error) {
        console.error('Error fetching user subscription:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserSubscription();
  }, [user]);

  const handleGoToUpgrade = useCallback(() => {
    navigate('/credits');
  }, [navigate]);

  const handleUpgradeClick = useMemo(() => {
    return () => setShowUpgradeDialog(true);
  }, []);

  // Handle tab change
  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value);
  }, []);

  // Memoize the content component to prevent re-renders
  const integrationsContent = useMemo(() => {
    if (loading || atsList.length === 0) {
      return (
        <div className="text-center py-10">
          <p className="text-muted-foreground">Loading integrations...</p>
        </div>
      );
    }
    
    return (
      <IntegrationsProxy 
        isBasicPlan={isBasicPlanUser} 
        onUpgradeClick={handleUpgradeClick}
        atsList={atsList}
      />
    );
  }, [loading, atsList, isBasicPlanUser, handleUpgradeClick]);

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      
      <main className="flex-1 overflow-y-auto">
        <div className="p-8">
          <div className="mb-8 animate-fade-in">
            <h1 className="text-3xl font-bold text-roundone-dark-purple mb-2">Settings</h1>
            <p className="text-muted-foreground">Manage your account settings and preferences</p>
          </div>
          
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle>Configuration</CardTitle>
              <p className="text-sm text-muted-foreground">Configure integrations and preferences</p>
            </CardHeader>
            <CardContent className="pt-4">
              <Tabs defaultValue="integrations" value={activeTab} onValueChange={handleTabChange}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="integrations">
                    Integrations
                    {isBasicPlanUser && (
                      <Badge className="ml-2 bg-amber-500 hover:bg-amber-600 text-[10px] px-2">Premium</Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="api" disabled className="relative">
                    API
                    <Badge className="ml-2 bg-amber-500 hover:bg-amber-600 absolute -top-2 -right-2 text-[10px] px-2">Coming Soon</Badge>
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="integrations" className="py-4" forceMount>
                  <style>
                    {`
                    .premium-overlay {
                      z-index: 10;
                      border-radius: 0.5rem;
                    }
                    `}
                  </style>
                  {integrationsContent}
                </TabsContent>
                
                <TabsContent value="api" className="py-4 space-y-6">
                  <div className="text-center text-muted-foreground py-20">
                    API settings will be available soon
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Upgrade Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-amber-500" />
              Premium Feature
            </DialogTitle>
            <DialogDescription>
              ATS integrations are available with Premium and Agency plans.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-orange-50 border border-orange-200 rounded-md p-4 my-2 text-orange-800 text-sm">
            <p className="font-medium">Upgrade your plan to connect with your ATS</p>
            <p className="mt-1 text-xs text-orange-700">
              Premium and Agency plans allow you to integrate with your ATS platform 
              to seamlessly manage candidates and automate assessment workflows.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpgradeDialog(false)}>Cancel</Button>
            <Button 
              variant="purple"
              className="bg-gradient-to-r from-[#9b87f5] to-[#7662DA] hover:from-[#8A76E5] hover:to-[#6552CA]"
              onClick={handleGoToUpgrade}
            >
              Upgrade Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
